import { isUserData, UserDataAndState } from "common/Database";
import { dontAwait } from "common/Support";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import { SignInUpModal } from "designer/modals/smart/sign-in-up-modal/sign-in-up-modal";
import { showBasicActionModal, showErrorModal } from "designer/ui/smart/modals/modals";
import { useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { RootState } from "store/types";
import { getAppFacilities } from "webapp/lib/app-renderer";
import once from "lodash/once";
import * as routes from "routes";

type DownloadExportProps = Readonly<{
    userData: UserDataAndState;
}> &
    RouteComponentProps<{ exportID: string }>;

async function showErrorAndPushRoot(e: string): Promise<void> {
    await showErrorModal(e);
    window.location.assign("/");
}

function DownloadExportScreenDisconnected(props: DownloadExportProps): JSX.Element {
    const { userData, match } = props;
    const { exportID } = match.params;

    const downloadProcess = useMemo(
        () =>
            once(() => {
                const op = async () => {
                    const response = await getAppFacilities().callAuthCloudFunction("requestDownloadLinkForExport", {
                        exportID,
                    });
                    if (response === undefined) {
                        return await showErrorAndPushRoot("Could not connect to Glide to request export file.");
                    }
                    if (response.status === 404) {
                        return await showErrorAndPushRoot("Export file is no longer available.");
                    }
                    if (response.status === 403) {
                        return await showErrorAndPushRoot("You do not have permission to download this export file.");
                    }
                    if (response.status !== 200) {
                        return await showErrorAndPushRoot(response.statusText);
                    }
                    const { downloadURL, appID, appTitle, canLink } = await response.json();
                    if (typeof downloadURL !== "string") {
                        return await showErrorAndPushRoot("Unexpected download response from the server.");
                    }

                    // (Good) browsers will not actually leave the page to download
                    // the export file; instead, they'll keep the current page context
                    // open until the headers are
                    setTimeout(async () => {
                        const header =
                            typeof appTitle === "string"
                                ? `Downloading Data from '${appTitle}'`
                                : "Downloading Data Export";
                        let firstLine = <span>Your data export is now being downloaded in the background.</span>;
                        if (typeof appTitle === "string") {
                            const forPart = canLink ? <a href={routes.app(appID)}>{appTitle}</a> : `'${appTitle}'`;
                            firstLine = (
                                <span>The data export for {forPart} is being downloaded in the background.</span>
                            );
                        }
                        await showBasicActionModal(
                            header,
                            <div>
                                <p>
                                    {firstLine} You may continue to use Glide normally while the download is in
                                    progress.
                                </p>
                                <p>
                                    If you do not see the downloaded file, or the download becomes stuck, try reloading
                                    this page.
                                </p>
                            </div>,
                            "Go Back to Glide"
                        );
                        window.location.assign("/");
                    }, 3000);
                    window.location.assign(downloadURL);
                };
                op().catch(e => dontAwait(showErrorModal(`${e}`)));
            }),
        [exportID]
    );

    useEffect(() => {
        if (!isUserData(userData)) return;

        downloadProcess();
    }, [userData, downloadProcess]);

    const doNothing = useCallback(() => {
        // Do nothing here.
        // The SignInUpModal will implicitly change our userData to something
        // defined.
    }, []);

    if (userData === null) {
        return <SignInUpModal onFinish={doNothing} />;
    } else {
        return <LoadingIndicator />;
    }
}

const mapState = (state: RootState) => ({
    userData: state.userData,
});

export const DownloadExportScreen = connect(mapState)(DownloadExportScreenDisconnected);
